import React, { useEffect, useMemo, useState } from "react";
import { accessApi } from "../../services/accessApi";

const EMPTY_PROFILE = {
  user_id: null,
  full_name: "",
  first_name: "",
  last_name: "",
  display_name: "",
  email: "",
  job_title: "",
  department: "",
  phone: "",
  mobile: "",
  website: "",
  company_name: "Modular Objects",
  company_address: "",
  city: "",
  country: "",
  linkedin_url: "",
  pinterest_url: "",
  twitter_url: "",
  whatsapp_url: "",
  telegram_url: "",
  youtube_url: "",
  instagram_url: "",
  facebook_url: "",
  vcard_url: "",
  tagline: "",
  logo_url: "",
  profile_image_url: "",
  accent_color: "#d6c176",
};

const HR_OWNED_FIELDS = new Set([
  "full_name",
  "first_name",
  "last_name",
  "display_name",
  "email",
  "job_title",
  "department",
  "phone",
  "mobile",
  "city",
  "country",
  "company_name",
]);

const FIELD_LABELS = [
  ["full_name", "Full Name"],
  ["first_name", "First Name"],
  ["last_name", "Last Name"],
  ["display_name", "Display Name"],
  ["email", "Email"],
  ["job_title", "Job Title"],
  ["department", "Department"],
  ["phone", "Phone"],
  ["mobile", "Mobile"],
  ["website", "Website"],
  ["company_name", "Company Name"],
  ["company_address", "Company Address"],
  ["city", "City"],
  ["country", "Country"],
  ["linkedin_url", "LinkedIn URL"],
  ["pinterest_url", "Pinterest URL"],
  ["twitter_url", "Twitter URL"],
  ["whatsapp_url", "WhatsApp URL"],
  ["telegram_url", "Telegram URL"],
  ["youtube_url", "YouTube URL"],
  ["instagram_url", "Instagram URL"],
  ["facebook_url", "Facebook URL"],
  ["vcard_url", "vCard URL"],
  ["tagline", "Tagline"],
  ["logo_url", "Logo URL"],
  ["profile_image_url", "Profile Image URL"],
  ["accent_color", "Accent Color"],
];

export default function ITMailSignatureProfilesPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(EMPTY_PROFILE);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await accessApi.getMailSignatureProfiles({ q });
      setRows(res?.data?.data || []);
    } catch (err) {
      console.error("ITMailSignatureProfilesPage load error:", err);
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to load signature profiles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      load();
    }, 250);

    return () => clearTimeout(t);
  }, [q]);

  const filteredRows = useMemo(() => rows, [rows]);

  const openEdit = async (userId) => {
    try {
      const res = await accessApi.getMailSignatureProfile(userId);
      const data = res?.data?.data || null;
      setDetail(data);

      const profile = data?.profile || {};
      const user = data?.user || {};
      const hr = data?.hr || {};

      setForm({
        ...EMPTY_PROFILE,
        ...profile,
        user_id: user.id,
        full_name: profile.full_name || hr.hr_full_name || user.display_name || "",
        display_name: profile.display_name || user.display_name || hr.hr_full_name || "",
        email: profile.email || user.email || "",
        job_title: profile.job_title || hr.position_title || "",
        department: profile.department || hr.department_name || "",
      });

      setEditorOpen(true);
    } catch (err) {
      console.error("openEdit signature profile error:", err);
      alert(err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to load profile.");
    }
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      await accessApi.updateMailSignatureProfile(form.user_id, form);
      setEditorOpen(false);
      await load();
    } catch (err) {
      console.error("saveProfile error:", err);
      alert(err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  const syncFromUsers = async () => {
    try {
      const res = await accessApi.syncMailSignatureProfilesFromUsers();
      alert(`Profiles synced from users. Affected: ${res?.data?.data?.affectedRows ?? 0}`);
      await load();
    } catch (err) {
      console.error("syncFromUsers error:", err);
      alert(err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to sync from users.");
    }
  };

  const syncFromHr = async () => {
    try {
      const res = await accessApi.syncMailSignatureProfilesFromHr();
      alert(`Profiles synced from HR. Processed: ${res?.data?.data?.ok ?? 0}`);
      await load();
    } catch (err) {
      console.error("syncFromHr error:", err);
      alert(err?.response?.data?.message || err?.response?.data?.error || err.message || "Failed to sync from HR.");
    }
  };

  const generateForUser = async (userId) => {
  try {
    const res = await accessApi.generateMailSignatureForUser(userId, {});
    const payload = res?.data?.data || res?.data || {};

    if (payload.blocked) {
      alert(
        `Signature generation blocked.\nMissing HR data: ${(payload.missing || []).join(", ")}\nHR notified: ${payload.notified_hr || 0}`
      );
      await load();
      return;
    }

    alert("Signature generated.");
    await load();
  } catch (err) {
    console.error("generateForUser error:", err);
    alert(
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err.message ||
      "Failed to generate signature."
    );
  }
};

  if (loading) return <div className="p-6 text-black dark:text-white">Loading profiles...</div>;
  if (error && !editorOpen) return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;

  const getReadiness = (row) => {
  const missing = [];

  if (!String(row.full_name || "").trim()) missing.push("full_name");
  if (!String(row.email || row.user_email || "").trim()) missing.push("email");
  if (!String(row.job_title || "").trim()) missing.push("job_title");
  if (!String(row.department || "").trim()) missing.push("department");

  return {
    ready: missing.length === 0,
    missing,
  };
};

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">Mail Signature Profiles</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
  HR manages core employee data. IT manages signature-specific branding fields and generation workflow.
</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={syncFromUsers}
            className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
          >
            Sync from Users
          </button>

          <button
            onClick={syncFromHr}
            className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
          >
            Sync from HR
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search users, email, title, department..."
          className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
        />
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1200px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Full Name</th>
                <th className="text-left p-3">Job Title</th>
                <th className="text-left p-3">Department</th>
                <th className="text-left p-3">Has Profile</th>
                <th className="text-left p-3">Generated Signatures</th>
                <th className="text-left p-3">Actions</th>
                <th className="text-left p-3">Readiness</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.user_id}
                  className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60"
                >
                  <td className="p-3 text-black dark:text-white font-medium">{row.display_name || row.user_display_name || "-"}</td>
                  <td className="p-3 text-black dark:text-white">{row.email || row.user_email || "-"}</td>
                  <td className="p-3 text-black dark:text-white">{row.full_name || "-"}</td>
                  <td className="p-3 text-black dark:text-white">{row.job_title || "-"}</td>
                  <td className="p-3 text-black dark:text-white">{row.department || "-"}</td>
                  <td className="p-3 text-black dark:text-white">{Number(row.has_profile) === 1 ? "Yes" : "No"}</td>
                  <td className="p-3 text-black dark:text-white">{row.generated_signatures_count || 0}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openEdit(row.user_id)}
                        className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
                      >
                        Edit
                      </button>

                      {(() => {
  const readiness = getReadiness(row);

  return (
    <button
  onClick={() => generateForUser(row.user_id)}
  title={!readiness.ready ? `Missing: ${readiness.missing.join(", ")}` : "Generate signature"}
  className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
>
  Generate
</button>
  );
})()}
                    </div>
                  </td>
                  <td className="p-3 text-black dark:text-white">
  {(() => {
    const readiness = getReadiness(row);
    return readiness.ready ? (
      <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
        Ready
      </span>
    ) : (
      <span
        title={`Missing: ${readiness.missing.join(", ")}`}
        className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300"
      >
        Missing data
      </span>
    );
  })()}
</td>
                </tr>
              ))}

              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    No profiles found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editorOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-neutral-800">
              <h2 className="text-xl font-bold text-black dark:text-white">Edit Signature Profile</h2>

              <button
                onClick={() => setEditorOpen(false)}
                className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
              >
                Close
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm mb-1">User</div>
                  <div className="rounded-xl border border-gray-200 dark:border-neutral-800 px-3 py-2 text-black dark:text-white bg-gray-50 dark:bg-neutral-900">
                    {detail?.user?.display_name || "-"} ({detail?.user?.email || "-"})
                  </div>
                </div>

                <div>
                  <div className="text-sm mb-1">HR</div>
                  <div className="rounded-xl border border-gray-200 dark:border-neutral-800 px-3 py-2 text-black dark:text-white bg-gray-50 dark:bg-neutral-900">
                    {detail?.hr
                      ? `${detail.hr.hr_full_name || "-"} / ${detail.hr.position_title || "-"} / ${detail.hr.department_name || "-"}`
                      : "No HR link"}
                  </div>
                  <div className="rounded-xl border border-blue-200 bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900 px-4 py-3 text-sm">
  Core identity fields are managed by HR and are read-only here. Missing required HR data will block signature generation and notify HR.
</div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {FIELD_LABELS.map(([key, label]) => {
  const isHrOwned = HR_OWNED_FIELDS.has(key);

  return (
    <div key={key}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-sm">{label}</div>
        {isHrOwned ? (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            Managed by HR
          </span>
        ) : (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
            Signature field
          </span>
        )}
      </div>

      <input
        value={form[key] || ""}
        readOnly={isHrOwned}
        onChange={(e) => {
          if (isHrOwned) return;
          setForm((prev) => ({ ...prev, [key]: e.target.value }));
        }}
        className={`w-full rounded-xl border px-3 py-2 text-black dark:text-white ${
          isHrOwned
            ? "border-gray-200 dark:border-neutral-800 bg-gray-100 dark:bg-neutral-900 cursor-not-allowed"
            : "border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950"
        }`}
      />
    </div>
  );
})}
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setEditorOpen(false)}
                  className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
                >
                  Cancel
                </button>

                <button
                  onClick={saveProfile}
                  disabled={saving}
                  className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Save Profile"}
                </button>

                <button
                  onClick={() => generateForUser(form.user_id)}
                  className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
                >
                  Generate Signature
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}